import React, { useEffect, useState } from "react";
import { getStats, startScan } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { SectionTitle, StatBlock, PrimaryButton, SecondaryButton, EmptyState } from "../components/UIKit";
import { Lightning, MapPin, Buildings, Handshake, Factory } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OverviewPage() {
  const { selected, countries } = useCountry();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);

  const fetchStats = async () => {
    try {
      const s = await getStats();
      setStats(s);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
    const iv = setInterval(fetchStats, 5000);
    return () => clearInterval(iv);
  }, []);

  const currentCountry = countries.find((c) => c.code === selected);

  const runScan = async (kind) => {
    setStarting(kind);
    try {
      const r = await startScan(kind, selected);
      if (r.already_running) {
        toast.info(`Scan already running for ${currentCountry?.name}.`);
      } else {
        toast.success(`Scan started for ${currentCountry?.flag} ${currentCountry?.name}. It will keep running in the background.`);
      }
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start scan");
    }
    setStarting(null);
  };

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline="COMMAND CENTER" title="Overview">
        <PrimaryButton
          testId="scan-builders-btn"
          onClick={() => runScan("builders")}
          disabled={!!starting}
        >
          <Lightning size={14} weight="bold" className="inline mr-1.5" />
          {starting === "builders" ? "STARTING…" : `SCAN INSTALLERS ${currentCountry?.flag || ""}`}
        </PrimaryButton>
        <SecondaryButton
          testId="scan-associations-btn"
          onClick={() => runScan("associations")}
          disabled={!!starting}
        >
          <Lightning size={14} weight="bold" />
          {starting === "associations" ? "STARTING…" : "SCAN BUYERS"}
        </SecondaryButton>
        <SecondaryButton
          testId="scan-manufacturers-btn"
          onClick={() => runScan("manufacturers")}
          disabled={!!starting}
        >
          <Lightning size={14} weight="bold" />
          {starting === "manufacturers" ? "STARTING…" : "SCAN MANUFACTURERS"}
        </SecondaryButton>
      </SectionTitle>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
        <StatBlock
          label="Installers"
          value={stats?.total_builders ?? "—"}
          hint="Inventory + potential buyers"
          testId="stat-builders"
          accent
        />
        <StatBlock
          label="Manufacturers"
          value={stats?.total_manufacturers ?? "—"}
          hint="Inventory (sold to installers)"
          testId="stat-manufacturers"
        />
        <StatBlock
          label="Buyers"
          value={stats?.total_associations ?? "—"}
          hint="Associations, distributors, etc."
          testId="stat-associations"
        />
        <StatBlock
          label="Live Scans"
          value={stats?.running_jobs ?? "—"}
          hint="Running in background"
          testId="stat-running"
        />
        <StatBlock
          label="Countries Active"
          value={stats?.per_country?.length ?? "—"}
          hint="With ≥1 result"
          testId="stat-countries"
        />
      </div>

      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            COVERAGE MAP
          </div>
          <h2 className="text-xl tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
            Results per country
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : stats?.per_country?.length ? (
        <div className="border border-black bg-white overflow-x-auto" data-testid="coverage-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black text-left">
                <th className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>Country</th>
                <th className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  <span className="inline-flex items-center gap-1"><Buildings size={12} weight="bold" /> Installers</span>
                </th>
                <th className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  <span className="inline-flex items-center gap-1"><Factory size={12} weight="bold" /> Mfg</span>
                </th>
                <th className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  <span className="inline-flex items-center gap-1"><Handshake size={12} weight="bold" /> Buyers</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.per_country
                .sort((a, b) => (b.builders + b.associations + (b.manufacturers || 0)) - (a.builders + a.associations + (a.manufacturers || 0)))
                .map((row) => (
                  <tr key={row.code} className="border-b border-black/10 last:border-b-0 hover:bg-black/[0.02]">
                    <td className="py-2 px-3">
                      <span className="mr-2">{row.flag}</span>
                      <span style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 600 }}>{row.name}</span>
                    </td>
                    <td className="py-2 px-3 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{row.builders}</td>
                    <td className="py-2 px-3 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{row.manufacturers ?? 0}</td>
                    <td className="py-2 px-3 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{row.associations}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<MapPin size={22} weight="bold" />}
          title="No data yet"
          hint={`Pick a country above and run "SCAN BUILDERS". The bot runs on the server — you can close this tab and come back.`}
        />
      )}
    </div>
  );
}
